// workspaceUrlSync.js — keeps the address bar naming the workspace the tab is
// actually in.
//
// `/w/<org-slug>` names only the ORG. In an org with two or more workspaces the
// URL was therefore ambiguous: `resolveActiveWorkspace` resolved it to the org's
// HQ workspace, so reloading a non-HQ workspace snapped back to HQ, a copied
// link opened the wrong workspace for the recipient, and two tabs could not hold
// two different workspaces of the same org. `?ws=<handle>` closes that gap; this
// module is the one place that writes it when the ACTIVE workspace changes.
//
// replaceState, never push: switching workspace is not a navigation — the route
// is unchanged and the user should not have to press Back twice to leave the
// page. It mirrors the address-bar canonicalize in mwtRouter.js, which is the
// established pattern here for "correct the URL without navigating" (`el.router`
// remains the only way to actually navigate — Rule 64).
//
// Pure-ish module, direct ESM import (workspacePrefix.js / guestRoutes.js
// pattern). Corrected 2026-08-12 (frank-trap sweep, tickets/sonnet.md) — the
// "not registered in functions/index.js, never frank-serialized" claim below
// was stale: it IS barrelled (`export * from './workspaceUrlSync.js'`), same
// stale-comment class the ticket already flagged for workspacePrefix.js.

import { isMwtEnabled } from './mwtFlag.js'
import {
  withWorkspaceQuery,
  withWorkspacePath,
  parseWorkspacePath,
  stripWorkspacePrefix
} from './workspacePrefix.js'
import { workspaceUrlHandle } from './activeWorkspacePointer.js'

// The workspace handle a URL should carry for the state's active workspace.
// ONE rule, shared by every stamper (syncWorkspaceUrl here, the mwtRouter's
// emitted-URL handle, projectHref's /p/ links), so the address bar can't
// disagree with itself.
export const resolveWorkspaceUrlHandle = (state) => {
  const ws = state?.workspace
  if (!ws) return null
  // EVERY workspace URL carries its segment — HQ included (2026-08-06 per
  // Nika: "make it consistant" → keep /w/, always stamp the ws segment; the
  // concise HQ short form is gone). This retires the HQ sibling heuristic
  // and its mid-switch org guard wholesale: the handle comes off the
  // workspace record alone, org-independent, exactly like the non-HQ arm
  // always worked. Bare /w/<org> links stay PARSED forever (they resolve to
  // the org's HQ) — the canonicalizer just rewrites them to the full shape
  // on the first sync.
  return workspaceUrlHandle(ws)
}

// Canonicalize the address bar to scheme A: `/w/<org>/<ws-handle>/<route…>`
// for EVERY workspace — HQ included (the concise HQ short form retired
// 2026-08-06 per Nika's consistency decision). Owns the workspace segment
// (resolveWorkspaceUrlHandle supplies it) and the legacy `?ws=` param
// (parsed on inbound links elsewhere, ALWAYS stripped here so every emitted
// URL is path-form).
//
// The ORG segment is DIRECTIONAL, so it moves only on explicit caller intent
// (`opts.rewriteOrg` — the org-switch handler, where state is the authority
// and the URL is the stale party). Everywhere else a path org that differs
// from the state org means the OPPOSITE authority order — a cross-org
// deep-link/reload whose state hasn't caught up yet (boot syncs before
// reconcileMwtOrgFromUrl client-switches to the URL's org) — and the sync
// no-ops entirely: rewriting there clobbered `/w/nikoloza?ws=…` back to the
// pointer org before the reconcile could run (caught live 2026-07-30).
//
// No-op when MWT is off: the prefix scheme it belongs to is not in play, so
// the URL shape stays byte-identical to today. Non-org-scoped paths
// (project-canvas `/<owner>/<key>`, auth, public surfaces) are workspace-
// INDEPENDENT and only self-clean a stale `?ws=` param — stamping the tab's
// ambient scope there produced URLs like `/natali/shop?ws=server`, whose
// foreign-org handle then poisoned workspace resolution on the next shell
// navigation (the "studio opened in the wrong workspace" drift).
export const syncWorkspaceUrl = (state, opts) => {
  if (!isMwtEnabled()) return null
  // FA204 / frank trap — moved from module scope into this function (sole
  // consumer of both — see longPress.js/dragRef.js for the identical shape).
  //
  // Workspace ids are always minted `ws_<nanoid>` (server/src/core/models/
  // Workspace.js: `key: () => \`ws_${nanoid(10)}\``, nanoid's URL-safe
  // alphabet — `[A-Za-z0-9_-]`). No intranet route ever starts with `ws_`
  // (mwtNav.js's INTRANET_RE), and workspace SLUGS are minted server-side
  // against a reserved blacklist mirroring the static route names — so this
  // shape is an unambiguous, safe signal that a path segment names a
  // workspace, independent of whether this session currently knows that
  // workspace by id.
  const RAW_WORKSPACE_ID_RE = /^ws_[A-Za-z0-9_-]+$/

  // Whether `seg` (a raw path segment) names a workspace this session should
  // recognize as stale/stray — the active workspace's own handle, ANY
  // sibling in `workspacesList`, OR the raw-id shape every workspace id is
  // minted in. See the original (pre-inline) header comment history for the
  // full "double-stamped URL" rationale (tickets/opus.md).
  const looksLikeStaleWorkspaceSegment = (seg, w0, workspacesList) => {
    if (!seg) return false
    const lower = seg.toLowerCase()
    // `default` — the HQ slug convention — is never a route either (see
    // workspacePrefix.js dropDefault). A boot sync that ran before this fix
    // stamped the handle in FRONT of it (`/w/system/hq/default`, "Not
    // found"); such a URL heals here like any other double stamp.
    if (lower === 'default') return true
    const known = [w0, ...(Array.isArray(workspacesList) ? workspacesList : [])]
    for (const w of known) {
      if (!w) continue
      const ids = [w.slug, w.key, w.id ?? w._id].filter(Boolean).map((v) => String(v).toLowerCase())
      if (ids.includes(lower)) return true
    }
    return RAW_WORKSPACE_ID_RE.test(seg)
  }

  const win = typeof window !== 'undefined' ? window : null
  if (!win?.history || !win.location) return null

  const ws = state?.workspace
  const path = win.location.pathname || '/'
  const orgScoped = path === '/w' || path.startsWith('/w/')

  let next
  if (!orgScoped || !ws) {
    // Off the org surface: only self-clean a stale legacy param.
    const current = path + (win.location.search || '')
    next = withWorkspaceQuery(current, null) + (win.location.hash || '')
  } else {
    const parsed = parseWorkspacePath(path)
    const stateSlug = state?.org?.slug || state?.org?.username || null
    const foreignOrgUrl = !!(parsed.orgSlug && stateSlug && parsed.orgSlug !== stateSlug)
    if (foreignOrgUrl && !opts?.rewriteOrg) return null
    const orgSlug = (opts?.rewriteOrg && stateSlug) || parsed.orgSlug || stateSlug || null
    // FA204 / frank trap — self-contained inline copy of resolveWorkspaceUrlHandle
    // (a bare sibling-export read is exactly as unresolvable post-frank as a
    // module-local read); the export above stays live for genuine external
    // consumers (meet/roomUrl.js, workspace/functions/mwtRouter.js).
    const handle = ws ? workspaceUrlHandle(ws) : null
    let route = parsed.route
    if (!parsed.wsHandle) {
      // Belt-and-suspenders: if the parse's resolver missed (a call racing
      // boot registration, or a genuinely stray/foreign/deleted workspace
      // segment the resolver never claims) and segment 2 looks like a
      // workspace identifier — strip it manually. Otherwise the rebuild
      // below double-stamps: either the SAME handle twice
      // (`/w/acme/team-b/team-b/tickets`, the original bug this guard
      // shipped for) or, when the stray segment names a DIFFERENT workspace
      // than the active one, the real handle gets stamped in FRONT of the
      // untouched stray id instead of replacing it
      // (`/w/acme/team-b/ws_<stale-id>/tickets`).
      //
      // LOOPED, not a single check: a cold boot that lands DIRECTLY on an
      // already double-stamped URL (`/w/<org>/<realHandle>/ws_<stale-id>/
      // <route>` — e.g. a Stripe `successUrl` captured while the address
      // bar was mid-corruption from an earlier, now-fixed run of this same
      // function) presents TWO consecutive workspace-like segments before
      // the real route starts. `bootShell.js`'s boot-time call always races
      // the resolver (registered later, in `app.js`'s `onCreate`), so the
      // primary `parseWorkspacePath` TWO_SEG_RE+resolver path can never
      // catch this — only this fallback can, and a single strip only ate
      // the first segment (`<realHandle>`) and left `ws_<stale-id>` glued
      // to the front of the rebuilt route, reconstructing the exact same
      // broken URL byte-for-byte (`next === current`, so the function
      // silently no-ops on an already-broken URL instead of healing it —
      // reproduced live against this exact module + Nika's exact reported
      // string; tickets/opus.md cold-boot regression). Bounded by the
      // route's own segment count so it can never run away, and a real
      // route segment can never false-positive here: server-side workspace
      // slugs are minted against a reserved blacklist mirroring the static
      // route names, and raw ids always carry the `ws_` shape prefix (this
      // file's own header comment) — neither can ever collide with an
      // actual route token, so stripping "while it still looks like a
      // workspace" is safe to repeat, not just do once.
      let seg = String(route.split('/')[1] || '')
      while (looksLikeStaleWorkspaceSegment(seg, ws, state?.workspaces)) {
        const rest = route.slice(seg.length + 1)
        route = rest === '' ? '/' : rest
        if (route === '/') break
        seg = String(route.split('/')[1] || '')
      }
    }
    const rebuilt = orgSlug ? withWorkspacePath(orgSlug, handle, route) : path
    next =
      withWorkspaceQuery(rebuilt + (win.location.search || ''), null) + (win.location.hash || '')
  }

  const current = path + (win.location.search || '') + (win.location.hash || '')
  if (next === current) return null
  try {
    win.history.replaceState(win.history.state, '', next)
  } catch {}
  return next
}
